'use client';

import { Link, Button } from '@/components/ui';
import { useLocale, useTranslations } from 'next-intl';
import { useState } from 'react';
import type { MatchWithRatingsV2 } from './types';
import { LocalDateTime } from '@/components/ui/LocalDateTime';
import { formatDate, formatTime } from '@/lib/utils';
import PlayerCard from '@/components/player/PlayerCard';
import { apiRequest } from '@/lib/api';

interface InProgressMatchCardProps {
  match: MatchWithRatingsV2;
  onDelete?: (matchId: string) => void;
}

export function InProgressMatchCard({ match, onDelete }: InProgressMatchCardProps) {
  const {
    id,
    dateISO,
    player1,
    player2,
    venue,
    competition,
    competitionDivision,
    isMarkedByPlayer: _isMarkedByPlayer,
  } = match;

  const locale = useLocale();
  const t = useTranslations('Matches');
  const [isAbandoning, setIsAbandoning] = useState(false);

  const handleAbandon = async () => {
    if (!onDelete) return;

    const confirmMsg = t('confirmAbandonMatch');
    if (!window.confirm(confirmMsg)) return;

    setIsAbandoning(true);
    try {
      await apiRequest(`/api/match/${id}/abandon`, { method: 'POST' });
      onDelete(id);
    } catch (error) {
      // 404: already gone (abandoned elsewhere) - the row should go too.
      // The route also answers 404 for a forbidden caller, by design (opaque).
      if ((error as { status?: number }).status === 404) {
        onDelete(id);
        return;
      }
      console.error('Error abandoning match:', error);
      alert(t('errorAbandoningMatch'));
    } finally {
      setIsAbandoning(false);
    }
  };

  return (
    <div className="rounded-xl border-2 border-orange-500/20 bg-gradient-to-br from-orange-500/10 to-amber-500/10 p-4 shadow-sm transition-all hover:shadow-md">
      {/* Header with date and status */}
      <div className="mb-3 flex items-start justify-between">
        <div>
          {/* `dateISO` is a timestamptz — see MatchCard. */}
          <LocalDateTime
            iso={dateISO}
            locale={locale}
            format={formatDate}
            className="text-foreground/90 block text-sm font-medium"
          />
          <LocalDateTime
            iso={dateISO}
            locale={locale}
            format={formatTime}
            className="text-muted block text-xs"
          />
          <div className="text-primary mt-1 flex items-center gap-2 text-xs font-medium">
            <div className="flex items-center gap-1">
              <div className="bg-primary h-1.5 w-1.5 animate-pulse rounded-full"></div>
              {t('inProgress')}
            </div>
          </div>
        </div>
        <div className="flex flex-col items-end gap-1.5">
          <Link href={`/match/score/${id}`} variant="filled" color="primary" size="sm">
            {t('continueScoring')}
          </Link>
          {onDelete && (
            <Button
              variant="outline"
              color="destructive"
              size="sm"
              onClick={handleAbandon}
              disabled={isAbandoning}
            >
              {isAbandoning ? t('abandoning') : t('abandon')}
            </Button>
          )}
        </div>
      </div>

      {/* Players Section */}
      <div className="mb-4 space-y-3">
        {player1 && (
          <PlayerCard
            player={{
              id: player1.id,
              username: player1.username,
              first_name: player1.first_name,
              last_name: player1.last_name,
              profile_photo: player1.profile_photo,
              current_rating: player1.current_rating,
            }}
            showRating={false}
            showUsername={true}
            size="md"
            clickable={true}
          />
        )}
        {player2 && (
          <PlayerCard
            player={{
              id: player2.id,
              username: player2.username,
              first_name: player2.first_name,
              last_name: player2.last_name,
              profile_photo: player2.profile_photo,
              current_rating: player2.current_rating,
            }}
            showRating={false}
            showUsername={true}
            size="md"
            clickable={true}
          />
        )}
      </div>

      {/* Event Context */}
      {(competition || (venue && venue !== '—')) && (
        <div className="bg-foreground/5 rounded-lg p-3">
          <div className="text-foreground/80 text-sm">
            {competition && (
              <div className="font-medium">
                {competition === 'Ranking Adjustment' ? t('rankingAdjustment') : competition}
                {competitionDivision && (
                  <span className="text-foreground/60 ml-1">• {competitionDivision}</span>
                )}
              </div>
            )}
            {venue && venue !== '—' && (
              <div className="text-foreground/60 mt-1 flex items-center gap-1">
                <span>📍</span>
                <span>{venue}</span>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
